import { Button, Display, Sprite } from "../../design/primitives";
import { art, sheetFor } from "../../design/sprites";
import css from "./states.module.css";

/**
 * The patron states, §7 and §9 - shared with Studio (FR-33).
 *
 * §9's voice rules are load-bearing here and easy to violate by reflex:
 * **no apologies, no exclamation marks.** Errors state what happened and what
 * to do; they do not say sorry.
 */

export function LoadingState({
  label = "TUNING UP",
  subLabel = "FETCHING YOUR LIBRARY",
  tint = "#4A9B9B",
}: {
  label?: string;
  subLabel?: string;
  tint?: string;
}) {
  return (
    <div className={css.wrap} role="status" aria-live="polite">
      <div className={css.bounce}>
        <Sprite src={sheetFor(tint)} size={104} />
      </div>
      <Display size={38} as="p">
        {label}
      </Display>
      <div className={css.dither} aria-hidden />
      <p className={css.subLabel}>{subLabel}</p>
    </div>
  );
}

export function NoResultsState({
  query,
  onClear,
}: {
  query: string;
  onClear: () => void;
}) {
  return (
    <div className={`${css.wrap} ${css.well}`}>
      <Sprite src={art.note} size={104} />
      <Display size={38} as="p">
        NOTHING MATCHES “{query.toUpperCase()}”
      </Display>
      <p className={css.copy}>
        Searching covers arrangement titles and game names. Try a shorter word,
        or clear the filters and browse.
      </p>
      <Button variant="secondary" size="state" onClick={onClear}>
        CLEAR FILTERS
      </Button>
    </div>
  );
}

export function ErrorState({
  headline = "THAT DID NOT LOAD",
  detail = "The library did not come back. Reloading usually settles it.",
  code,
  onRetry,
}: {
  headline?: string;
  detail?: string;
  code?: string;
  onRetry: () => void;
}) {
  return (
    <div className={css.wrap} role="alert">
      <Sprite src={art.crtErr} size={104} />
      <Display size={38} as="p">
        {headline}
      </Display>
      <p className={css.copy}>{detail}</p>
      <Button variant="gold" size="state" onClick={onRetry}>
        TRY AGAIN
      </Button>
      {code && <p className={css.code}>{code}</p>}
    </div>
  );
}

/**
 * First run: a surface with nothing in it yet — FR-33.
 *
 * Distinct from `NoResultsState`, which means "your filter matched nothing".
 * This means "there is nothing", and the difference matters: one is undone by
 * clearing a search, the other by doing some work. Saying the wrong one sends
 * the reader looking for a filter that is not set.
 *
 * §9's voice: it says what happens once you act, not that something is absent.
 * "No assets" is a fact; "publishing puts it in the library straight away" is
 * a reason to press the button.
 */
export function EmptyState({
  sprite,
  headline,
  detail,
  actionLabel,
  onAction,
}: {
  sprite: string;
  headline: string;
  detail: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className={css.wrap}>
      {/* 104px, like every other state. Studio's hand-rolled version used 150
          and its own padding, which is how two surfaces stop looking related. */}
      <Sprite src={sprite} size={104} />
      <Display size={38} as="p">
        {headline}
      </Display>
      <p className={css.copy}>{detail}</p>
      {actionLabel && onAction && (
        <Button variant="gold" size="state" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
}
